import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ClipboardList, 
  UserPlus, 
  Trash2, 
  Mail, 
  Phone, 
  MapPin,
  MessageSquare,
  Pencil,
  Check,
  CheckCircle,
  X,
  Loader2,
  RefreshCw,
  Plus,
  Search,
  Building2,
  Users,
  CalendarPlus,
  Ticket as TicketIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import TicketCreateDialog from '../components/TicketCreateDialog';
import { FileText } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { findPostcodeByCity, getSuggestions } from '../lib/belgianPostcodes';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { aanvragenAPI, salesAPI, appointmentAPI } from '../lib/api';
import api from '../lib/api';
import { formatPhoneNumber } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

const statusConfig = {
  new: { label: 'Nieuw', color: 'bg-blue-500', textColor: 'text-blue-500' },
  contacted: { label: 'Gecontacteerd', color: 'bg-yellow-500', textColor: 'text-yellow-500' },
  converted: { label: 'Geconverteerd', color: 'bg-green-500', textColor: 'text-green-500' },
  rejected: { label: 'Afgewezen', color: 'bg-red-500', textColor: 'text-red-500' },
};

const Aanvragen = () => {
  const [aanvragen, setAanvragen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const getAanvraagType = (a) => {
    const rt = (a.request_type || '').toLowerCase();
    if (rt.includes('reinig') || rt.includes('nettoy') || rt.includes('clean')) return 'reiniging';
    if (rt.includes('batterij') || rt.includes('batter')) return 'thuisbatterij';
    if (rt.includes('zonnepa') || rt.includes('installatie') || rt.includes('pv') || rt.includes('solar')) return 'installatie';
    return 'overige';
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [aanvraagToDelete, setAanvraagToDelete] = useState(null);
  const [converting, setConverting] = useState(null);
  const [creatingOfferte, setCreatingOfferte] = useState(null);

  const handleCreateOfferte = async (aanvraag) => {
    setCreatingOfferte(aanvraag.id);
    try {
      const res = await aanvragenAPI.createOfferte(aanvraag.id);
      toast.success(`Offerte ${res.data.offerte_number} aangemaakt!`);
      fetchAanvragen();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon offerte niet aanmaken');
    } finally {
      setCreatingOfferte(null);
    }
  };
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [aanvraagToConvert, setAanvraagToConvert] = useState(null);
  const [convertFormData, setConvertFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
    street: '',
    number: '',
    postal_code: '',
    city: '',
    country: 'België',
  });
  const [newAanvraag, setNewAanvraag] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
    street: '',
    number: '',
    postal_code: '',
    city: '',
    request_type: '',
    message: '',
    assigned_to: '', // Will be set to current user on dialog open
  });
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  // Sales team state for assignment
  const [salesUsers, setSalesUsers] = useState([]);
  const [assigningId, setAssigningId] = useState(null);
  const [filterAssignedTo, setFilterAssignedTo] = useState('all');
  
  // City autocomplete state
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);

  const fetchAanvragen = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterAssignedTo && filterAssignedTo !== 'all') {
        if (filterAssignedTo === 'unassigned') {
          // We'll filter client-side for unassigned
        } else {
          params.assigned_to = filterAssignedTo;
        }
      }
      const response = await aanvragenAPI.getAll(params);
      let data = response.data;
      
      // Client-side filter for unassigned
      if (filterAssignedTo === 'unassigned') {
        data = data.filter(a => !a.assigned_to);
      }
      
      setAanvragen(data);
    } catch (error) {
      console.error('Error fetching aanvragen:', error);
      toast.error('Kon aanvragen niet laden');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSalesUsers = async () => {
    try {
      const response = await salesAPI.getUsers();
      setSalesUsers(response.data);
    } catch (error) {
      console.error('Error fetching sales users:', error);
    }
  };

  useEffect(() => {
    fetchAanvragen();
    if (isAdmin) {
      fetchSalesUsers();
    }
  }, [filterAssignedTo]);

  const handleAssign = async (aanvraagId, userId) => {
    try {
      setAssigningId(aanvraagId);
      if (userId === 'unassign') {
        await aanvragenAPI.unassign(aanvraagId);
        toast.success('Toewijzing verwijderd');
      } else {
        await aanvragenAPI.assign(aanvraagId, userId);
        const assignedUser = salesUsers.find(u => u.id === userId);
        toast.success(`Toegewezen aan ${assignedUser?.first_name || 'medewerker'}`);
      }
      fetchAanvragen();
    } catch (error) {
      console.error('Error assigning:', error);
      toast.error('Kon niet toewijzen');
    } finally {
      setAssigningId(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await aanvragenAPI.create(newAanvraag);
      toast.success('Aanvraag toegevoegd');
      setDialogOpen(false);
      setNewAanvraag({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company_name: '',
        street: '',
        number: '',
        postal_code: '',
        city: '',
        request_type: '',
        message: '',
        assigned_to: '',
      });
      fetchAanvragen();
    } catch (error) {
      toast.error('Kon aanvraag niet toevoegen');
    }
  };

  const handleDelete = (aanvraag) => {
    setAanvraagToDelete(aanvraag);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!aanvraagToDelete) return;
    try {
      await aanvragenAPI.delete(aanvraagToDelete.id);
      toast.success('Aanvraag verwijderd');
      fetchAanvragen();
    } catch (error) {
      toast.error('Kon aanvraag niet verwijderen');
    } finally {
      setDeleteConfirmOpen(false);
      setAanvraagToDelete(null);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await aanvragenAPI.updateStatus(id, newStatus);
      toast.success(`Status bijgewerkt naar ${statusConfig[newStatus].label}`);
      fetchAanvragen();
    } catch (error) {
      toast.error('Kon status niet bijwerken');
    }
  };

  // Opmerking bewerken
  const [messageEdit, setMessageEdit] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [savingMessage, setSavingMessage] = useState(false);

  const openMessageEdit = (aanvraag) => {
    setMessageEdit(aanvraag);
    setMessageText(aanvraag.message || '');
  };

  const saveMessage = async () => {
    setSavingMessage(true);
    try {
      await aanvragenAPI.updateMessage(messageEdit.id, messageText);
      toast.success('Opmerking opgeslagen');
      setMessageEdit(null);
      fetchAanvragen();
    } catch (error) {
      toast.error('Kon opmerking niet opslaan');
    }
    setSavingMessage(false);
  };

  // Open convert dialog with aanvraag data pre-filled
  const [salesApptDialog, setSalesApptDialog] = useState(null);
  const [savingAppt, setSavingAppt] = useState(false);
  const [confirmMailDialog, setConfirmMailDialog] = useState(null);
  const [mailSending, setMailSending] = useState(null);

  const handleConfirmationMail = async (mode) => {
    const d = confirmMailDialog;
    const mailType = d.mode === 'cancel' ? 'Annuleringsmail' : d.mode === 'reschedule' ? 'Wijzigingsmail' : 'Bevestigingsmail';
    setMailSending(mode);
    try {
      const res = await api.post(`/appointments/${d.appointmentId}/confirmation-email`, {
        to_email: d.toEmail,
        to_name: d.toName,
        language: d.language || 'nl',
        mode: d.mode || 'confirm',
      });
      if (mode === 'send') {
        await api.post(`/email-queue/${res.data.email_id}/send`);
        toast.success(`${mailType} verzonden naar ${d.toEmail}`);
      } else {
        toast.success(`${mailType} klaargezet bij Mails — handmatig te versturen`);
      }
      setConfirmMailDialog(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || `${mailType} klaarzetten mislukt`);
    } finally {
      setMailSending(null);
    }
  };

  const [reschedDialog, setReschedDialog] = useState(null);
  const [cancelApptDialog, setCancelApptDialog] = useState(null);
  const [apptActionBusy, setApptActionBusy] = useState(false);

  const apptMailDialogFor = (aanvraag, appointmentId, mode) => ({
    appointmentId,
    toEmail: aanvraag.email,
    toName: `${aanvraag.first_name || ''} ${aanvraag.last_name || ''}`.trim(),
    language: (aanvraag.language || 'nl').toLowerCase().slice(0, 2),
    mode,
  });

  const openReschedule = (aanvraag) => {
    const ap = aanvraag.sales_appointment;
    setReschedDialog({ aanvraag, appointmentId: ap.id, date: ap.date || '', time: ap.time || '', end_time: ap.end_time || '' });
  };

  const handleReschedTimeChange = (t) => {
    let end = '';
    if (t) {
      const [h, m] = t.split(':').map(Number);
      const total = h * 60 + m + 60;
      end = `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
    }
    setReschedDialog(prev => ({ ...prev, time: t, end_time: end }));
  };

  const handleReschedule = async () => {
    const d = reschedDialog;
    if (!d.date || !d.time) { toast.error('Kies een datum en starttijd'); return; }
    setApptActionBusy(true);
    try {
      await api.post(`/appointments/${d.appointmentId}/reschedule`, { date: d.date, time: d.time, end_time: d.end_time || undefined });
      toast.success('Afspraak verzet');
      setReschedDialog(null);
      fetchAanvragen();
      if (d.aanvraag.email) setConfirmMailDialog(apptMailDialogFor(d.aanvraag, d.appointmentId, 'reschedule'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Afspraak verzetten mislukt');
    } finally {
      setApptActionBusy(false);
    }
  };

  const handleCancelAppointment = async () => {
    const d = cancelApptDialog;
    setApptActionBusy(true);
    try {
      await api.post(`/appointments/${d.appointmentId}/cancel`);
      toast.success('Afspraak geannuleerd');
      setCancelApptDialog(null);
      fetchAanvragen();
      if (d.aanvraag.email) setConfirmMailDialog(apptMailDialogFor(d.aanvraag, d.appointmentId, 'cancel'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Afspraak annuleren mislukt');
    } finally {
      setApptActionBusy(false);
    }
  };

  const [ticketAanvraag, setTicketAanvraag] = useState(null);

  const openSalesAppointment = (aanvraag) => {
    const address = [aanvraag.street, aanvraag.number, aanvraag.postal_code, aanvraag.city].filter(Boolean).join(' ');
    const name = `${aanvraag.first_name || ''} ${aanvraag.last_name || ''}`.trim();
    const description = [
      address ? `Adres: ${address}` : null,
      aanvraag.phone ? `Tel: ${aanvraag.phone}` : null,
      aanvraag.email ? `Email: ${aanvraag.email}` : null,
      aanvraag.message ? `Opmerking: ${aanvraag.message}` : null,
    ].filter(Boolean).join('\n');
    setSalesApptDialog({
      aanvraag,
      title: `Sales afspraak - ${name}${aanvraag.city ? ` (${aanvraag.city})` : ''}`,
      date: new Date().toISOString().slice(0, 10),
      time: '',
      end_time: '',
      assigned_to: aanvraag.assigned_to || user?.id || '',
      description,
    });
  };

  const handleApptTimeChange = (t) => {
    let end = '';
    if (t) {
      const [h, m] = t.split(':').map(Number);
      const total = h * 60 + m + 60;
      end = `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
    }
    setSalesApptDialog(prev => ({ ...prev, time: t, end_time: end }));
  };

  const handleCreateSalesAppointment = async () => {
    const d = salesApptDialog;
    if (!d.date || !d.time) {
      toast.error('Kies een datum en starttijd');
      return;
    }
    setSavingAppt(true);
    try {
      const assignedUser = salesUsers.find(u => u.id === d.assigned_to);
      const assignedName = assignedUser
        ? `${assignedUser.first_name} ${assignedUser.last_name}`
        : (d.assigned_to === user?.id ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim() : undefined);
      const res = await appointmentAPI.create({
        title: d.title,
        appointment_type: 'sales',
        date: d.date,
        time: d.time,
        end_time: d.end_time || undefined,
        customer_id: d.aanvraag.customer_id || undefined,
        aanvraag_id: d.aanvraag.id,
        assigned_to: d.assigned_to || undefined,
        assigned_to_name: assignedName,
        description: d.description,
      });
      toast.success('Sales afspraak ingepland');
      setSalesApptDialog(null);
      // Aanvraag automatisch op "Gecontacteerd" zetten
      if (d.aanvraag.status === 'new') {
        try {
          await aanvragenAPI.updateStatus(d.aanvraag.id, 'contacted');
        } catch (e) { /* status-update niet blokkerend */ }
      }
      fetchAanvragen();
      if (d.aanvraag.email) {
        setConfirmMailDialog({
          appointmentId: res.data.id,
          toEmail: d.aanvraag.email,
          toName: `${d.aanvraag.first_name || ''} ${d.aanvraag.last_name || ''}`.trim(),
          language: (d.aanvraag.language || 'nl').toLowerCase().slice(0, 2),
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon afspraak niet inplannen');
    } finally {
      setSavingAppt(false);
    }
  };

  const openConvertDialog = (aanvraag) => {
    setAanvraagToConvert(aanvraag);
    setConvertFormData({
      first_name: aanvraag.first_name || '',
      last_name: aanvraag.last_name || '',
      email: aanvraag.email || '',
      phone: aanvraag.phone || '',
      company_name: aanvraag.company_name || '',
      street: aanvraag.street || '',
      number: aanvraag.number || '',
      postal_code: aanvraag.postal_code || '',
      city: aanvraag.city || '',
      country: aanvraag.country || 'België',
    });
    setConvertDialogOpen(true);
  };

  const handleConvert = async () => {
    if (!aanvraagToConvert) return;
    
    setConverting(aanvraagToConvert.id);
    try {
      // Send the updated form data along with the convert request
      const response = await aanvragenAPI.convert(aanvraagToConvert.id, convertFormData);
      toast.success(response.data.message);
      setConvertDialogOpen(false);
      setAanvraagToConvert(null);
      fetchAanvragen();
      // Navigate to the new customer
      if (response.data.customer_id) {
        navigate(`/customers/${response.data.customer_id}`);
      }
    } catch (error) {
      toast.error('Kon aanvraag niet converteren naar klant');
    } finally {
      setConverting(null);
    }
  };

  // Filter aanvragen
  const filteredAanvragen = aanvragen.filter(a => {
    const matchesSearch = 
      `${a.first_name} ${a.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
      a.email?.toLowerCase().includes(search.toLowerCase()) ||
      a.city?.toLowerCase().includes(search.toLowerCase()) ||
      a.phone?.includes(search);
    
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    const matchesType = typeFilter === 'all' || getAanvraagType(a) === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // Stats
  const stats = {
    total: aanvragen.length,
    new: aanvragen.filter(a => a.status === 'new').length,
    contacted: aanvragen.filter(a => a.status === 'contacted').length,
    converted: aanvragen.filter(a => a.status === 'converted').length,
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('nl-BE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <ClipboardList className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
            Aanvragen
          </h1>
          <p className="text-sm text-muted-foreground">Leads van website en andere bronnen</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={fetchAanvragen}>
            <RefreshCw className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Vernieuwen</span>
          </Button>
          <Button size="sm" onClick={() => {
            // Pre-fill with current user as assigned
            setNewAanvraag(prev => ({
              ...prev,
              assigned_to: user?.id || '',
              first_name: '',
              last_name: '',
              email: '',
              phone: '',
              company_name: '',
              street: '',
              number: '',
              postal_code: '',
              city: '',
              request_type: '',
              message: '',
            }));
            setDialogOpen(true);
          }}>
            <Plus className="w-4 h-4 mr-2" />
            Nieuwe Aanvraag
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-card/50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Totaal</div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-500">{stats.new}</div>
            <div className="text-sm text-muted-foreground">Nieuw</div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-l-4 border-l-yellow-500">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-500">{stats.contacted}</div>
            <div className="text-sm text-muted-foreground">Gecontacteerd</div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-500">{stats.converted}</div>
            <div className="text-sm text-muted-foreground">Geconverteerd</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Zoek op naam, email, telefoon of stad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="type-filter">
            <SelectValue placeholder="Filter op type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle types</SelectItem>
            <SelectItem value="reiniging">Reiniging</SelectItem>
            <SelectItem value="installatie">Installatie</SelectItem>
            <SelectItem value="thuisbatterij">Thuisbatterij</SelectItem>
            <SelectItem value="overige">Overige</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
            <SelectValue placeholder="Filter op status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            <SelectItem value="new">Nieuw</SelectItem>
            <SelectItem value="contacted">Gecontacteerd</SelectItem>
            <SelectItem value="converted">Geconverteerd</SelectItem>
            <SelectItem value="rejected">Afgewezen</SelectItem>
          </SelectContent>
        </Select>
        {isAdmin && (
          <Select value={filterAssignedTo} onValueChange={setFilterAssignedTo}>
            <SelectTrigger className="w-full md:w-48" data-testid="assigned-filter">
              <SelectValue placeholder="Filter op medewerker" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle medewerkers</SelectItem>
              <SelectItem value="unassigned">Niet toegewezen</SelectItem>
              {salesUsers.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Aanvragen List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filteredAanvragen.length === 0 ? (
        <Card className="bg-card/50">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Geen aanvragen gevonden</p>
            <p className="text-sm text-muted-foreground">
              {search || statusFilter !== 'all' 
                ? 'Pas je filters aan of voeg een nieuwe aanvraag toe'
                : 'Aanvragen van de website verschijnen hier automatisch'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredAanvragen.map((aanvraag) => (
            <Card 
              key={aanvraag.id} 
              className={`bg-card/50 hover:bg-card/80 transition-colors max-w-full overflow-hidden ${
                aanvraag.status === 'converted' ? 'opacity-60' : ''
              }`}
              data-testid={`aanvraag-card-${aanvraag.id}`}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col gap-3">
                  {/* Header: Name + Status */}
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-base sm:text-lg truncate">
                        {aanvraag.first_name} {aanvraag.last_name}
                      </h3>
                      {aanvraag.company_name && (
                        <span className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1">
                          <Building2 className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{aanvraag.company_name}</span>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 flex-wrap">
                      {aanvraag.is_existing_customer && (
                        <Badge variant="outline" className="border-orange-500 text-orange-500 text-xs">
                          Bestaande klant
                        </Badge>
                      )}
                      <Badge className={`${statusConfig[aanvraag.status]?.color} text-white text-xs`}>
                        {statusConfig[aanvraag.status]?.label || aanvraag.status}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Contact Info */}
                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
                    {aanvraag.email && (
                      <span className="flex items-center gap-1 min-w-0">
                        <Mail className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <a href={`mailto:${aanvraag.email}`} className="hover:text-primary truncate">
                          {aanvraag.email}
                        </a>
                      </span>
                    )}
                    {aanvraag.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <a href={`tel:${aanvraag.phone}`} className="hover:text-primary">
                          {formatPhoneNumber(aanvraag.phone)}
                        </a>
                      </span>
                    )}
                  </div>
                  
                  {/* Address */}
                  {(aanvraag.street || aanvraag.city) && (
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        [aanvraag.street, aanvraag.number, aanvraag.postal_code, aanvraag.city]
                          .filter(Boolean)
                          .join(' ')
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors"
                      data-testid={`maps-link-${aanvraag.id}`}
                    >
                      <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">
                        {[aanvraag.street, aanvraag.number, aanvraag.postal_code, aanvraag.city]
                          .filter(Boolean)
                          .join(' ')}
                      </span>
                    </a>
                  )}

                  {aanvraag.request_type && (
                    <div className="text-xs sm:text-sm">
                      <span className="text-muted-foreground">Type aanvraag: </span>
                      <span className="font-medium">{aanvraag.request_type}</span>
                    </div>
                  )}

                  <div className="text-xs sm:text-sm bg-muted/50 p-2 rounded-md break-words overflow-hidden group/msg flex items-start gap-1" data-testid={`aanvraag-message-${aanvraag.id}`}>
                    <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span className="break-words flex-1">{aanvraag.message || <span className="text-muted-foreground italic">Geen opmerking</span>}</span>
                    <button
                      onClick={() => openMessageEdit(aanvraag)}
                      className="flex-shrink-0 p-1 rounded hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                      title="Opmerking bewerken"
                      data-testid={`edit-message-btn-${aanvraag.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {aanvraag.message_updated_by_name && (
                    <p className="text-[11px] text-muted-foreground -mt-1">Opmerking bijgewerkt door {aanvraag.message_updated_by_name}{aanvraag.message_updated_at ? ` op ${formatDate(aanvraag.message_updated_at)}` : ''}</p>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs text-muted-foreground">
                    <span className="truncate">Ontvangen: {formatDate(aanvraag.created_at)} via {aanvraag.source || 'onbekend'}</span>
                    {aanvraag.assigned_to_name && (
                      <span className="flex items-center gap-1 text-primary">
                        <Users className="w-3 h-3" />
                        {aanvraag.assigned_to_name}
                      </span>
                    )}
                    {!aanvraag.assigned_to && (
                      <span className="text-orange-500">Niet toegewezen</span>
                    )}
                  </div>

                  {aanvraag.sales_appointment && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-800 dark:text-blue-300" data-testid={`sales-appt-info-${aanvraag.id}`}>
                      <span className="flex items-center gap-1.5 font-medium">
                        <CalendarPlus className="w-3.5 h-3.5 flex-shrink-0" />
                        Sales afspraak: {new Date(aanvraag.sales_appointment.date + 'T00:00:00').toLocaleDateString('nl-BE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                        {aanvraag.sales_appointment.time ? ` om ${aanvraag.sales_appointment.time}` : ''}
                        {aanvraag.sales_appointment.end_time ? ` - ${aanvraag.sales_appointment.end_time}` : ''}
                      </span>
                      {aanvraag.sales_appointment.assigned_to_name && (
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />door {aanvraag.sales_appointment.assigned_to_name}</span>
                      )}
                      {aanvraag.sales_appointment.created_by_name && (
                        <span className="text-blue-600/70 dark:text-blue-400/60">ingepland door {aanvraag.sales_appointment.created_by_name}</span>
                      )}
                      <span className="flex items-center gap-1.5 ml-auto">
                        <button
                          onClick={() => openReschedule(aanvraag)}
                          className="px-2 py-0.5 rounded border border-blue-300 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/40 font-medium"
                          data-testid={`resched-appt-btn-${aanvraag.id}`}
                        >
                          Verzetten
                        </button>
                        <button
                          onClick={() => setCancelApptDialog({ aanvraag, appointmentId: aanvraag.sales_appointment.id })}
                          className="px-2 py-0.5 rounded border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 font-medium"
                          data-testid={`cancel-appt-btn-${aanvraag.id}`}
                        >
                          Annuleren
                        </button>
                      </span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-row md:flex-col gap-2">
                    {/* Show "Klant toegevoegd" badge if customer_id exists, otherwise show convert button */}
                    {aanvraag.customer_id ? (
                      <div className="flex items-center gap-2 px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-md text-sm font-medium">
                        <CheckCircle className="w-4 h-4" />
                        <span className="hidden sm:inline">Klant toegevoegd</span>
                        <span className="sm:hidden">✓</span>
                      </div>
                    ) : aanvraag.status !== 'converted' && (
                      <Button
                        onClick={() => openConvertDialog(aanvraag)}
                        disabled={converting === aanvraag.id}
                        className={aanvraag.is_existing_customer 
                          ? "bg-orange-600 hover:bg-orange-700" 
                          : "bg-green-600 hover:bg-green-700"}
                        data-testid={`convert-btn-${aanvraag.id}`}
                      >
                        {converting === aanvraag.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <UserPlus className="w-4 h-4 mr-2" />
                        )}
                        <span className="hidden sm:inline">
                          {aanvraag.is_existing_customer ? 'Adres toevoegen' : 'Toevoegen als klant'}
                        </span>
                        <span className="sm:hidden">
                          {aanvraag.is_existing_customer ? 'Adres' : 'Klant'}
                        </span>
                      </Button>
                    )}

                    {aanvraag.contract_type && (
                      aanvraag.offerte_number ? (
                        <div className="flex items-center gap-2 px-3 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-md text-sm font-medium" data-testid={`offerte-created-${aanvraag.id}`}>
                          <FileText className="w-4 h-4" />
                          <span>{aanvraag.offerte_number}</span>
                        </div>
                      ) : (
                        <Button
                          onClick={() => handleCreateOfferte(aanvraag)}
                          disabled={creatingOfferte === aanvraag.id}
                          className="bg-purple-600 hover:bg-purple-700"
                          data-testid={`create-offerte-btn-${aanvraag.id}`}
                        >
                          {creatingOfferte === aanvraag.id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <FileText className="w-4 h-4 mr-2" />
                          )}
                          <span className="hidden sm:inline">Maak offerte</span>
                          <span className="sm:hidden">Offerte</span>
                        </Button>
                      )
                    )}
                    
                    <Button
                      variant="outline"
                      className="border-blue-500/50 text-blue-600 hover:text-blue-700 hover:bg-blue-500/10"
                      onClick={() => openSalesAppointment(aanvraag)}
                      data-testid={`plan-sales-btn-${aanvraag.id}`}
                    >
                      <CalendarPlus className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Sales afspraak</span>
                      <span className="sm:hidden">Afspraak</span>
                    </Button>

                    <Button
                      variant="outline"
                      className="border-amber-500/50 text-amber-600 hover:text-amber-700 hover:bg-amber-500/10"
                      onClick={() => setTicketAanvraag(aanvraag)}
                      data-testid={`create-ticket-btn-${aanvraag.id}`}
                    >
                      <TicketIcon className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Ticket maken</span>
                      <span className="sm:hidden">Ticket</span>
                    </Button>

                    <Select 
                      value={aanvraag.status} 
                      onValueChange={(value) => handleStatusChange(aanvraag.id, value)}
                    >
                      <SelectTrigger className="w-full md:w-40" data-testid={`status-select-${aanvraag.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">Nieuw</SelectItem>
                        <SelectItem value="contacted">Gecontacteerd</SelectItem>
                        <SelectItem value="rejected">Afgewezen</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Assignment dropdown - Admin only */}
                    {isAdmin && (
                      <Select 
                        value={aanvraag.assigned_to || 'unassigned'}
                        onValueChange={(value) => handleAssign(aanvraag.id, value === 'unassigned' ? 'unassign' : value)}
                        disabled={assigningId === aanvraag.id}
                      >
                        <SelectTrigger className="w-full md:w-44" data-testid={`assign-select-${aanvraag.id}`}>
                          {assigningId === aanvraag.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <SelectValue placeholder="Toewijzen aan..." />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Niet toegewezen</SelectItem>
                          {salesUsers.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.first_name} {u.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    <Button
                      variant="outline"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(aanvraag)}
                      data-testid={`delete-btn-${aanvraag.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Aanvraag Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nieuwe Aanvraag</DialogTitle>
            <DialogDescription>Voeg handmatig een lead/aanvraag toe</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Voornaam *</Label>
                <Input
                  required
                  value={newAanvraag.first_name}
                  onChange={(e) => setNewAanvraag({...newAanvraag, first_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Achternaam *</Label>
                <Input
                  required
                  value={newAanvraag.last_name}
                  onChange={(e) => setNewAanvraag({...newAanvraag, last_name: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  required
                  value={newAanvraag.email}
                  onChange={(e) => setNewAanvraag({...newAanvraag, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Telefoon</Label>
                <Input
                  value={newAanvraag.phone}
                  onChange={(e) => setNewAanvraag({...newAanvraag, phone: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Bedrijfsnaam</Label>
              <Input
                value={newAanvraag.company_name}
                onChange={(e) => setNewAanvraag({...newAanvraag, company_name: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-2">
                <Label>Straat</Label>
                <Input
                  value={newAanvraag.street}
                  onChange={(e) => setNewAanvraag({...newAanvraag, street: e.target.value})}
                />
              </div>
              <div>
                <Label>Nr</Label>
                <Input
                  value={newAanvraag.number}
                  onChange={(e) => setNewAanvraag({...newAanvraag, number: e.target.value})}
                />
              </div>
              <div>
                <Label>Postcode</Label>
                <Input
                  value={newAanvraag.postal_code}
                  onChange={(e) => setNewAanvraag({...newAanvraag, postal_code: e.target.value})}
                />
              </div>
            </div>
            <div className="relative">
              <Label>Gemeente</Label>
              <Input
                value={newAanvraag.city}
                onChange={(e) => {
                  const city = e.target.value;
                  setNewAanvraag(prev => ({
                    ...prev, 
                    city,
                  }));
                  // Show suggestions dropdown
                  setCitySuggestions(getSuggestions(city));
                  setShowCitySuggestions(true);
                }}
                onFocus={() => {
                  if (newAanvraag.city.length >= 2) {
                    setCitySuggestions(getSuggestions(newAanvraag.city));
                    setShowCitySuggestions(true);
                  }
                }}
                onBlur={(e) => {
                  // Delay hiding to allow click on suggestion
                  setTimeout(() => setShowCitySuggestions(false), 200);
                  // On blur, try to find postcode
                  const postcode = findPostcodeByCity(e.target.value, false);
                  if (postcode && !newAanvraag.postal_code) {
                    setNewAanvraag(prev => ({ ...prev, postal_code: postcode }));
                  }
                }}
                placeholder="Typ gemeente..."
                autoComplete="off"
              />
              {/* City suggestions dropdown */}
              {showCitySuggestions && citySuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {citySuggestions.map((suggestion, index) => (
                    <div
                      key={`${suggestion.postcode}-${suggestion.city}-${index}`}
                      className="px-3 py-2 hover:bg-muted cursor-pointer flex justify-between items-center"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setNewAanvraag(prev => ({
                          ...prev,
                          city: suggestion.city,
                          postal_code: suggestion.postcode
                        }));
                        setShowCitySuggestions(false);
                      }}
                    >
                      <span>{suggestion.city}</span>
                      <span className="text-muted-foreground text-sm">{suggestion.postcode}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Type Aanvraag</Label>
              <Select 
                value={newAanvraag.request_type} 
                onValueChange={(value) => setNewAanvraag({...newAanvraag, request_type: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="zonnepanelen">Zonnepanelen</SelectItem>
                  <SelectItem value="batterij">Batterij</SelectItem>
                  <SelectItem value="laadpaal">Laadpaal</SelectItem>
                  <SelectItem value="reiniging">Reiniging</SelectItem>
                  <SelectItem value="onderhoud">Onderhoud</SelectItem>
                  <SelectItem value="andere">Andere</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Toewijzen aan</Label>
              <Select 
                value={newAanvraag.assigned_to} 
                onValueChange={(value) => setNewAanvraag({...newAanvraag, assigned_to: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer medewerker" />
                </SelectTrigger>
                <SelectContent>
                  {salesUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Standaard: jij ({user?.first_name} {user?.last_name})
              </p>
            </div>
            <div>
              <Label>Bericht/Opmerking</Label>
              <Textarea
                value={newAanvraag.message}
                onChange={(e) => setNewAanvraag({...newAanvraag, message: e.target.value})}
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuleren
              </Button>
              <Button type="submit">Toevoegen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aanvraag verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je de aanvraag van {aanvraagToDelete?.first_name} {aanvraagToDelete?.last_name} wilt verwijderen? 
              Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setAanvraagToDelete(null)}>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Convert to Customer Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {aanvraagToConvert?.is_existing_customer ? 'Adres toevoegen' : 'Toevoegen als klant'}
            </DialogTitle>
            <DialogDescription>
              Controleer en vul de gegevens aan voordat je {aanvraagToConvert?.is_existing_customer ? 'het adres toevoegt' : 'deze lead als klant toevoegt'}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="convert_first_name">Voornaam *</Label>
              <Input
                id="convert_first_name"
                value={convertFormData.first_name}
                onChange={(e) => setConvertFormData({...convertFormData, first_name: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="convert_last_name">Achternaam *</Label>
              <Input
                id="convert_last_name"
                value={convertFormData.last_name}
                onChange={(e) => setConvertFormData({...convertFormData, last_name: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="convert_email">E-mail *</Label>
              <Input
                id="convert_email"
                type="email"
                value={convertFormData.email}
                onChange={(e) => setConvertFormData({...convertFormData, email: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="convert_phone">Telefoon</Label>
              <Input
                id="convert_phone"
                value={convertFormData.phone}
                onChange={(e) => setConvertFormData({...convertFormData, phone: e.target.value})}
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label htmlFor="convert_company">Bedrijfsnaam</Label>
              <Input
                id="convert_company"
                value={convertFormData.company_name}
                onChange={(e) => setConvertFormData({...convertFormData, company_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="convert_street">Straat</Label>
              <Input
                id="convert_street"
                value={convertFormData.street}
                onChange={(e) => setConvertFormData({...convertFormData, street: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="convert_number">Huisnummer</Label>
              <Input
                id="convert_number"
                value={convertFormData.number}
                onChange={(e) => setConvertFormData({...convertFormData, number: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="convert_postal_code">Postcode</Label>
              <Input
                id="convert_postal_code"
                value={convertFormData.postal_code}
                onChange={(e) => setConvertFormData({...convertFormData, postal_code: e.target.value})}
                className={!convertFormData.postal_code ? 'border-orange-500' : ''}
              />
              {!convertFormData.postal_code && (
                <p className="text-xs text-orange-500">Postcode ontbreekt - vul deze in</p>
              )}
            </div>
            <div className="space-y-2 relative">
              <Label htmlFor="convert_city">Stad</Label>
              <Input
                id="convert_city"
                value={convertFormData.city}
                onChange={(e) => {
                  const city = e.target.value;
                  setConvertFormData({...convertFormData, city});
                  setCitySuggestions(getSuggestions(city));
                  setShowCitySuggestions(true);
                }}
                onFocus={() => {
                  if (convertFormData.city.length >= 2) {
                    setCitySuggestions(getSuggestions(convertFormData.city));
                    setShowCitySuggestions(true);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setShowCitySuggestions(false), 200);
                  const postcode = findPostcodeByCity(convertFormData.city, false);
                  if (postcode && !convertFormData.postal_code) {
                    setConvertFormData(prev => ({ ...prev, postal_code: postcode }));
                  }
                }}
                autoComplete="off"
              />
              {showCitySuggestions && citySuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {citySuggestions.map((suggestion, index) => (
                    <div
                      key={`convert-${suggestion.postcode}-${suggestion.city}-${index}`}
                      className="px-3 py-2 hover:bg-muted cursor-pointer flex justify-between items-center"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setConvertFormData(prev => ({
                          ...prev,
                          city: suggestion.city,
                          postal_code: suggestion.postcode
                        }));
                        setShowCitySuggestions(false);
                      }}
                    >
                      <span>{suggestion.city}</span>
                      <span className="text-muted-foreground text-sm">{suggestion.postcode}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="convert_country">Land</Label>
              <Input
                id="convert_country"
                value={convertFormData.country}
                onChange={(e) => setConvertFormData({...convertFormData, country: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConvertDialogOpen(false)}>
              Annuleren
            </Button>
            <Button 
              onClick={handleConvert}
              disabled={converting === aanvraagToConvert?.id || !convertFormData.first_name || !convertFormData.last_name || !convertFormData.email}
              className={aanvraagToConvert?.is_existing_customer ? "bg-orange-600 hover:bg-orange-700" : "bg-green-600 hover:bg-green-700"}
            >
              {converting === aanvraagToConvert?.id ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Bezig...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  {aanvraagToConvert?.is_existing_customer ? 'Adres toevoegen' : 'Toevoegen als klant'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Opmerking bewerken dialog */}
      <Dialog open={!!messageEdit} onOpenChange={(o) => !o && setMessageEdit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              <Pencil className="w-5 h-5 inline mr-2" />
              Opmerking bewerken — {messageEdit?.first_name} {messageEdit?.last_name}
            </DialogTitle>
            <DialogDescription>Houd hier bij wat de status is of wat de klant exact wenst.</DialogDescription>
          </DialogHeader>
          <Textarea
            rows={6}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Bijv. gebeld op 20/08 — klant wil offerte voor 12 panelen + thuisbatterij, terugbellen volgende week…"
            data-testid="message-edit-textarea"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setMessageEdit(null)}>Annuleren</Button>
            <Button onClick={saveMessage} disabled={savingMessage} data-testid="message-edit-save-btn">
              {savingMessage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket maken vanuit aanvraag */}
      <TicketCreateDialog
        open={!!ticketAanvraag}
        onOpenChange={(o) => { if (!o) setTicketAanvraag(null); }}
        prefill={ticketAanvraag ? {
          title: `${ticketAanvraag.first_name || ''} ${ticketAanvraag.last_name || ''}`.trim() + (ticketAanvraag.city ? ` (${ticketAanvraag.city})` : ''),
          description: [
            [ticketAanvraag.street, ticketAanvraag.number, ticketAanvraag.postal_code, ticketAanvraag.city].filter(Boolean).length ? `Adres: ${[ticketAanvraag.street, ticketAanvraag.number, ticketAanvraag.postal_code, ticketAanvraag.city].filter(Boolean).join(' ')}` : null,
            ticketAanvraag.phone ? `Tel: ${ticketAanvraag.phone}` : null,
            ticketAanvraag.email ? `Email: ${ticketAanvraag.email}` : null,
            ticketAanvraag.message ? `Opmerking: ${ticketAanvraag.message}` : null,
          ].filter(Boolean).join('\n'),
          customer_id: ticketAanvraag.customer_id || '',
        } : {}}
      />

      {/* Sales afspraak inplannen */}
      <Dialog open={!!salesApptDialog} onOpenChange={(o) => !o && setSalesApptDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="sales-appt-dialog">
          <DialogHeader>
            <DialogTitle>Sales afspraak inplannen</DialogTitle>
            <DialogDescription>Gegevens zijn vooraf ingevuld vanuit de aanvraag</DialogDescription>
          </DialogHeader>
          {salesApptDialog && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Titel</Label>
                <Input
                  value={salesApptDialog.title}
                  onChange={(e) => setSalesApptDialog(prev => ({ ...prev, title: e.target.value }))}
                  data-testid="sales-appt-title"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Datum</Label>
                  <Input
                    type="date"
                    value={salesApptDialog.date}
                    onChange={(e) => setSalesApptDialog(prev => ({ ...prev, date: e.target.value }))}
                    data-testid="sales-appt-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Start</Label>
                  <Input
                    type="time"
                    value={salesApptDialog.time}
                    onChange={(e) => handleApptTimeChange(e.target.value)}
                    data-testid="sales-appt-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Einde</Label>
                  <Input
                    type="time"
                    value={salesApptDialog.end_time}
                    onChange={(e) => setSalesApptDialog(prev => ({ ...prev, end_time: e.target.value }))}
                    data-testid="sales-appt-end-time"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">Duurtijd standaard 1 uur — pas de eindtijd aan indien nodig.</p>
              <div className="space-y-2">
                <Label>Toegewezen aan</Label>
                <Select
                  value={salesApptDialog.assigned_to || 'none'}
                  onValueChange={(v) => setSalesApptDialog(prev => ({ ...prev, assigned_to: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger data-testid="sales-appt-assigned">
                    <SelectValue placeholder="Kies medewerker" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Niet toegewezen</SelectItem>
                    {user?.id && !salesUsers.some(u => u.id === user.id) && (
                      <SelectItem value={user.id}>{user.first_name} {user.last_name} (ik)</SelectItem>
                    )}
                    {salesUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Beschrijving</Label>
                <Textarea
                  rows={5}
                  value={salesApptDialog.description}
                  onChange={(e) => setSalesApptDialog(prev => ({ ...prev, description: e.target.value }))}
                  data-testid="sales-appt-description"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSalesApptDialog(null)}>Annuleren</Button>
            <Button onClick={handleCreateSalesAppointment} disabled={savingAppt} data-testid="sales-appt-save-btn">
              {savingAppt ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarPlus className="w-4 h-4 mr-2" />}
              Inplannen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Afspraak verzetten */}
      <Dialog open={!!reschedDialog} onOpenChange={(o) => !o && setReschedDialog(null)}>
        <DialogContent className="max-w-md" data-testid="resched-appt-dialog">
          <DialogHeader>
            <DialogTitle>Afspraak verzetten</DialogTitle>
            <DialogDescription>Kies een nieuwe datum en tijd — de klant kan daarna per mail verwittigd worden</DialogDescription>
          </DialogHeader>
          {reschedDialog && (
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Datum</Label>
                <Input type="date" value={reschedDialog.date}
                  onChange={(e) => setReschedDialog(prev => ({ ...prev, date: e.target.value }))}
                  data-testid="resched-appt-date" />
              </div>
              <div className="space-y-2">
                <Label>Start</Label>
                <Input type="time" value={reschedDialog.time}
                  onChange={(e) => handleReschedTimeChange(e.target.value)}
                  data-testid="resched-appt-time" />
              </div>
              <div className="space-y-2">
                <Label>Einde</Label>
                <Input type="time" value={reschedDialog.end_time}
                  onChange={(e) => setReschedDialog(prev => ({ ...prev, end_time: e.target.value }))}
                  data-testid="resched-appt-end-time" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReschedDialog(null)}>Sluiten</Button>
            <Button onClick={handleReschedule} disabled={apptActionBusy} data-testid="resched-appt-save-btn">
              {apptActionBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarPlus className="w-4 h-4 mr-2" />}
              Verzetten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Afspraak annuleren */}
      <Dialog open={!!cancelApptDialog} onOpenChange={(o) => !o && setCancelApptDialog(null)}>
        <DialogContent className="max-w-md" data-testid="cancel-appt-dialog">
          <DialogHeader>
            <DialogTitle>Afspraak annuleren?</DialogTitle>
            <DialogDescription>
              De sales afspraak wordt geannuleerd en verdwijnt uit de agenda. De klant kan daarna per mail verwittigd worden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCancelApptDialog(null)}>Behouden</Button>
            <Button variant="destructive" onClick={handleCancelAppointment} disabled={apptActionBusy} data-testid="cancel-appt-confirm-btn">
              {apptActionBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Afspraak annuleren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bevestigingsmail na inplannen */}
      <Dialog open={!!confirmMailDialog} onOpenChange={(o) => !o && setConfirmMailDialog(null)}>
        <DialogContent className="max-w-md" data-testid="confirm-mail-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirmMailDialog?.mode === 'cancel' ? 'Afspraak geannuleerd' : confirmMailDialog?.mode === 'reschedule' ? 'Afspraak verzet ✓' : 'Afspraak ingepland ✓'}
            </DialogTitle>
            <DialogDescription>
              {confirmMailDialog?.mode === 'cancel' ? 'Annuleringsmail' : confirmMailDialog?.mode === 'reschedule' ? 'Wijzigingsmail' : 'Bevestigingsmail'} sturen naar <span className="font-medium text-foreground">{confirmMailDialog?.toEmail}</span>?
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Taal van de mail:</span>
            {['nl', 'fr', 'en'].map((l) => (
              <Button
                key={l}
                type="button"
                size="sm"
                variant={(confirmMailDialog?.language || 'nl') === l ? 'default' : 'outline'}
                onClick={() => setConfirmMailDialog(prev => ({ ...prev, language: l }))}
                data-testid={`confirm-mail-lang-${l}`}
              >
                {l.toUpperCase()}
              </Button>
            ))}
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => handleConfirmationMail('send')}
              disabled={!!mailSending}
              data-testid="confirm-mail-send-btn"
            >
              {mailSending === 'send' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Meteen versturen
            </Button>
            <Button
              variant="outline"
              onClick={() => handleConfirmationMail('queue')}
              disabled={!!mailSending}
              data-testid="confirm-mail-queue-btn"
            >
              {mailSending === 'queue' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardList className="w-4 h-4 mr-2" />}
              Klaarzetten bij Mails (handmatig versturen)
            </Button>
            <Button
              variant="ghost"
              onClick={() => setConfirmMailDialog(null)}
              disabled={!!mailSending}
              data-testid="confirm-mail-none-btn"
            >
              Geen mail sturen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Aanvragen;
